import type {
  AnalyticsApplicationStatus,
  CheckReferencesActionOrigins,
  TRACKING_CANDIDATE_DETAILS,
} from 'src/constants/pageTrackingData';
import { createPageAnalyticsFn } from 'src/libs/Analytics';

export type CheckReferencesActionOriginsProfileDrawer = Extract<
  CheckReferencesActionOrigins,
  'limited reference check banner' | 'candidate profile reference check pressed'
>;

export type TrackingCandidateDetails = typeof TRACKING_CANDIDATE_DETAILS;

type CandidateManagementLinkAnalyticEvents =
  | {
      event: 'banner_content_pressed';
      attributes: {
        bannerElement:
          | 'Go to Verifications Tab'
          | 'Go to Why Verify SEEK Pass Page';
        displayedBanner:
          | 'Career History Verification Banner'
          | 'Candidate Management - Verified by SEEK Pass Banner';
        seekListingId: number;
        candidateId: number;
      };
    }
  | {
      event: 'references_tab_pressed';
      attributes: {
        isSeekReferenceApplication: boolean;
        referencesReceived: `${number}:${number}`;
        bucket: AnalyticsApplicationStatus;
        candidateId: number;
        prospectId: string;
        applicationCorrelationId: string;
        seekListingId: number;
        applicationSeekReferenceStatus: 'standard' | 'limited' | null;
      };
    }
  | {
      event: 'verifications_tab_pressed';
      attributes: {
        seekListingId: number;
        candidateId: number;
      };
    }
  | {
      event: 'alert_dismiss_pressed';
      attributes: {
        alertShownMessageCode: 'Candidate Management - Verified by SEEK Pass Banner';
        seekListingId: number;
        candidateId: number;
      };
    }
  | {
      event: 'learn_how_credential_are_verified_pressed';
      attributes: {
        actionOrigin:
          | 'Candidate Management Profile - Verification Tab'
          | 'Candidate Management Profile - Profile Tab';
      };
    }
  | {
      event: 'why_this_could_happen_pressed';
      attributes: {
        actionOrigin: 'Candidate Management Profile - Verification Tab';
      };
    }
  | {
      event: 'dialog_dismiss_pressed';
      attributes: {
        isSeekReferenceJob: boolean;
        seekReferenceBalance: number | null;
        bucket: AnalyticsApplicationStatus;
        currentPage: TrackingCandidateDetails;
        seekListingId: number;
        candidateId: number;
        prospectId: string;
        applicationCorrelationId: string;
        dialogName:
          | 'Candidate Management - Order a Reference Check'
          | 'Candidate Management - Reference Check Requested'
          | 'Candidate Management - Request a Limited Reference Check'
          | 'Candidate Management - Limited Reference Check Requested'
          | 'Candidate Management - Unlock Reference Report'
          | 'Candidate Management - Unlock Full Reference Check Upgrade Now'
          | null;
        jobSeekReferenceStatus: 'standard' | 'limited' | null;
      };
    }
  | {
      event: 'order_reference_check_pressed';
      attributes: {
        isSeekReferenceJob: boolean;
        seekReferenceBalance: number | null;
        bucket: AnalyticsApplicationStatus;
        currentPage: TrackingCandidateDetails;
        seekListingId: number;
        candidateId: number;
        prospectId: string;
        applicationCorrelationId: string;
        jobSeekReferenceStatus: 'limited' | 'standard' | null;
        linkAction:
          | 'Order Reference Check'
          | 'Request a Limited Reference Check'
          | 'Unlock Reference Check'
          | null;
        linkText: 'Order reference check' | 'unlock reference';
        dialogName:
          | 'Candidate Management - Order a Reference Check'
          | 'Candidate Management - Request a Limited Reference Check'
          | 'Candidate Management - Unlock Reference Report'
          | null;
      };
    }
  | {
      event: 'dialog_dismiss_pressed';
      attributes: {
        seekListingId: number;
        currentPage: TrackingCandidateDetails;
        dialogName: "Candidate Management - SEEKRef - Couldn't check references";
      };
    }
  | {
      event: 'purchase_reference_check_add_on_pressed';
      attributes: {
        actionOrigin: CheckReferencesActionOriginsProfileDrawer;
        isSeekReferenceJob: boolean;
        jobSeekReferenceStatus: 'limited' | 'standard' | null;
        seekReferenceBalance: number | null;
        bucket: AnalyticsApplicationStatus;
        currentPage: TrackingCandidateDetails;
        seekListingId: number;
        candidateId: number;
        prospectId: string;
        applicationCorrelationId: string;
        linkAction: 'Go To Purchase Reference Check Add On Modal';
        linkText: 'purchase add on';
        dialogName: 'Candidate Management - Unlock Full Reference Check Upgrade Now';
      };
    }
  | {
      event: 'reference_check_pressed';
      attributes: {
        isSeekReferenceJob: boolean;
        seekReferenceBalance: number | null;
        applicationCorrelationId: string;
        bucket: AnalyticsApplicationStatus;
        candidateId: number;
        prospectId: string;
        currentPage: TrackingCandidateDetails;
        jobSeekReferenceStatus: 'standard' | 'limited' | null;
        seekListingId: number;
      };
    }
  | {
      event: 'banner_content_pressed';
      attributes: {
        bannerElement:
          | 'Reference Check Add On Banner'
          | 'Go to Unlock Reference Report Dialog Box';
        displayedBanner:
          | 'Limited Reference Check Banner'
          | 'Unlock Standard Reference Banner';
        seekListingId: number;
        isSeekReferenceJob: boolean;
        jobSeekReferenceStatus: 'standard' | 'limited' | null;
        seekReferenceBalance: number | null;
        bucket: AnalyticsApplicationStatus;
        currentPage: TrackingCandidateDetails;
        candidateId: number;
        prospectId: string;
        applicationCorrelationId: string;
      };
    }
  | {
      event: 'upgrade_reference_pressed';
      attributes: {
        isSeekReferenceJob: boolean;
        jobSeekReferenceStatus: 'standard' | 'limited' | null;
        seekReferenceBalance: number | null;
        bucket: AnalyticsApplicationStatus;
        currentPage: TrackingCandidateDetails;
        candidateId: number;
        prospectId: string;
        applicationCorrelationId: string;
        seekListingId: number;
        dialogName:
          | 'Candidate Management - Unlock Reference Report'
          | 'Candidate Management - Unlock Full Reference Check Upgrade Now';
      };
    }
  | {
      event: 'content_element_pressed';
      attributes: {
        elementDesign: 'notice';
        elementText: 'View the report';
        elementAction: 'Redirection to reference report';
      };
    };

type CandidateManagementViewAnalyticEvents =
  | {
      event: 'candidate_management_banner_displayed';
      attributes: {
        displayedBanner: 'Career History Verification Banner';
        seekListingId: number;
        candidateId: number;
      };
    }
  | {
      event: 'dialog_displayed';
      attributes: {
        isSeekReferenceJob: boolean;
        seekReferenceBalance: number | null;
        bucket: AnalyticsApplicationStatus;
        currentPage: TrackingCandidateDetails;
        seekListingId: number;
        candidateId: number;
        prospectId: string;
        applicationCorrelationId: string;
        dialogName:
          | 'Candidate Management - Order a Reference Check'
          | 'Candidate Management - Reference Check Requested'
          | 'Candidate Management - Request a Limited Reference Check'
          | 'Candidate Management - Limited Reference Check Requested'
          | 'Candidate Management - Unlock Reference Report'
          | null;
        jobSeekReferenceStatus: 'standard' | 'limited' | null;
      };
    }
  | {
      event: 'dialog_displayed';
      attributes: {
        seekListingId: number;
        currentPage: TrackingCandidateDetails;
        dialogName: "Candidate Management - SEEKRef - Couldn't check references";
      };
    }
  | {
      event: 'dialog_displayed';
      attributes: {
        dialogName: 'Candidate Management - Unlock Full Reference Check Upgrade Now';
        applicationCorrelationId: string;
        candidateId: number;
        prospectId: string;
        seekListingId: number;
        currentPage: TrackingCandidateDetails;
        bucket: AnalyticsApplicationStatus;
        seekReferenceBalance: number | null;
        jobSeekReferenceStatus: 'limited' | 'standard' | null;
        isSeekReferenceJob: boolean;
        actionOrigin: CheckReferencesActionOriginsProfileDrawer;
      };
    }
  | {
      event: 'content_element_viewed';
      attributes: {
        elementDesign: 'notice';
        elementText: 'Reference available';
      };
    };

export const createPageAnalytics = createPageAnalyticsFn<
  CandidateManagementLinkAnalyticEvents,
  CandidateManagementViewAnalyticEvents
>({
  siteSubsection: 'candidate',
});

export type ProfileDrawerPageAnalytics = ReturnType<typeof createPageAnalytics>;
